import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import Dynamipay, { SERVICES } from 'services/dynamipay';
import { filterAllowedAggregators } from 'utils/moduleVisibility';

const STORAGE_KEY = 'global.aggregator';

const readStoredAggregator = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || null;
  } catch (error) {
    return null;
  }
};

const AggregatorContext = createContext({
  aggregators: [],
  isLoading: false,
  selectedAggregatorId: null,
  selectedAggregator: null,
  setSelectedAggregatorId: () => {},
  selectorOpenRequest: 0,
  requestSelectorOpen: () => {},
});

/**
 * Estado GLOBAL del agregador (rediseño): un solo selector en el sidebar
 * en lugar del card repetido por vista. Mismo origen de datos y filtro de
 * permisos que el AggregatorSelector anterior (COMPANIES +
 * filterAllowedAggregators). Persiste en sessionStorage.
 */
export function AggregatorProvider({ children }) {
  const [aggregators, setAggregators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAggregatorId, setSelectedIdState] = useState(readStoredAggregator);

  // Contador de "solicitudes de apertura" del selector del sidebar
  // (lo usa el botón "Elegir agregador" de los estados vacíos).
  const [selectorOpenRequest, setSelectorOpenRequest] = useState(0);

  const requestSelectorOpen = useCallback(() => {
    setSelectorOpenRequest((prev) => prev + 1);
  }, []);

  const setSelectedAggregatorId = useCallback((value) => {
    const normalized =
      value === null || value === undefined || value === '' ? null : String(value);

    setSelectedIdState(normalized);

    try {
      if (normalized) {
        sessionStorage.setItem(STORAGE_KEY, normalized);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) { /* almacenamiento no disponible */ }
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadAggregators = async () => {
      setIsLoading(true);

      try {
        const response = await Dynamipay(SERVICES.COMPANIES).get();

        const options =
          response.values?.data?.map((item) => ({
            id: item.company.id,
            name: item.company.name,
          })) || [];

        // Filtrar agregadores según configuración del cliente
        const filtered = filterAllowedAggregators(options);

        if (!mounted) return;

        setAggregators(filtered);
      } catch (error) {
        console.error('[AggregatorProvider]', error);
        if (mounted) setAggregators([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadAggregators();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedAggregator = useMemo(
    () =>
      aggregators.find(
        (item) => String(item.id) === String(selectedAggregatorId),
      ) || null,
    [aggregators, selectedAggregatorId],
  );

  const value = useMemo(
    () => ({
      aggregators,
      isLoading,
      selectedAggregatorId,
      selectedAggregator,
      setSelectedAggregatorId,
      selectorOpenRequest,
      requestSelectorOpen,
    }),
    [
      aggregators,
      isLoading,
      selectedAggregatorId,
      selectedAggregator,
      setSelectedAggregatorId,
      selectorOpenRequest,
      requestSelectorOpen,
    ],
  );

  return (
    <AggregatorContext.Provider value={value}>
      {children}
    </AggregatorContext.Provider>
  );
}

AggregatorProvider.propTypes = {
  children: PropTypes.any,
};

export const useAggregator = () => useContext(AggregatorContext);

export default AggregatorContext;
